import React, { useEffect, useRef, useState, useCallback, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { getStatusConfig } from '../../utils/statusBadgeConfig';
import {
  searchContacts,
  searchTransactionDonors,
  searchSubscriptionDonors,
  fetchRecentTransactions,
  fetchContactSubscriptions,
  searchForms,
  searchElements,
  fetchFormsByNames,
  fetchElementsByNames,
} from '../../utils/RocksetQueries/SearchQueries';

interface Contact {
  name: string;
  email: string;
  contactID: string;
}

interface Transaction {
  transactionID: string;
  timestamp: string;
  totalCharged: number;
  state: string;
  dashboardDonorName: string;
  email: string;
  contactID?: string;
}

interface Subscription {
  id: string;
  totalCharged: number;
  subscriptionStatus: string;
  donationFrequency: string;
  dashboardDonorName: string;
  email: string;
  contactID?: string;
}

interface PaymentContact extends Contact {
  transactions: Transaction[];
}

interface SubscriptionContact extends Contact {
  subscriptions: Subscription[];
}

interface FormResult {
  elementID: string;
  formName: string;
  formType: string;
  active: boolean;
}

interface ElementResult {
  elementID: string;
  elementName: string;
  elementType: string;
  active: boolean;
}

interface SearchResults {
  contacts: Contact[];
  payments: PaymentContact[];
  subscriptions: SubscriptionContact[];
  forms: FormResult[];
  elements: ElementResult[];
}

const EMPTY_RESULTS: SearchResults = {
  contacts: [],
  payments: [],
  subscriptions: [],
  forms: [],
  elements: [],
};
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const MAX_PER_CATEGORY = 3;
const MAX_TRANSACTIONS_PER_CONTACT = 3;

interface UniversalSearchProps {
  merchant: { merchantName?: string; [key: string]: any } | null | undefined;
  placeholder?: string;
  compact?: boolean;
  showShortcut?: boolean;
  showShortcutTooltip?: boolean;
  collapseToIconBelowLg?: boolean;
  collapseToIconAtLg?: boolean;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

const SearchShortcutTooltip = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3">
    <span>{label}</span>
    <span className="flex items-center gap-0.5 rounded border border-edge bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-ink">
      <span aria-hidden="true">⌘</span>
      <span>K</span>
    </span>
  </div>
);

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFrequency(freq: string): string {
  if (!freq) return '';
  const map: Record<string, string> = {
    ONE_TIME: '',
    DAILY: '/day',
    WEEKLY: '/wk',
    MONTHLY: '/mo',
    ANNUALLY: '/yr',
  };
  return map[freq] ?? '';
}

function formatFormType(type: string): string {
  const map: Record<string, string> = {
    FUNDRAISER: 'Standard',
    EMBED_FORM: 'Standard',
    CROWDFUNDING_FORM: 'Giving Levels',
    PRODUCT: 'Sponsorship',
    EVENT: 'Event',
  };
  return map[type] ?? type;
}

function formatElementType(type: string): string {
  const map: Record<string, string> = {
    ADD_TO_CART: 'Add to Cart',
    FUNDRAISING_BAR: 'Fundraising Bar',
    QR_CODE: 'QR Code',
    DONOR_LIST: 'Donor List',
    FUNDRAISING_FLAG: 'Fundraising Flag',
  };
  return map[type] ?? type;
}

type BadgeVariant = 'success' | 'warning' | 'danger' | 'neutral';

function StatusBadge({ status }: { status: string }) {
  const { formattedState, badgeVariant } = getStatusConfig(status);
  return (
    <Badge variant={badgeVariant as BadgeVariant} className="text-[10px] px-1.5 py-0.5">
      {formattedState}
    </Badge>
  );
}

const UniversalSearch = React.forwardRef<{ open: () => void }, UniversalSearchProps>(
  function UniversalSearch(
    {
      merchant,
      placeholder = 'Search for contacts, payments, subscriptions, forms, or elements',
      compact = false,
      showShortcut = true,
      showShortcutTooltip = false,
      collapseToIconBelowLg = false,
      collapseToIconAtLg = false,
      tooltipSide = 'bottom',
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchGenRef = useRef(0);
    const history = useHistory();

    const merchantName = merchant?.merchantName;
    const isEnabled = Boolean(merchantName);

    // Expose open() method for external triggers (e.g., mobile search icon)
    useImperativeHandle(
      ref,
      () => ({
        open: () => isEnabled && setOpen(true),
      }),
      [isEnabled],
    );

    // Cmd+K global shortcut
    useEffect(() => {
      if (!isEnabled) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          setOpen((prev) => !prev);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isEnabled]);

    // Cleanup debounce on unmount
    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, []);

    const executeSearch = useCallback(
      async (keyword: string) => {
        if (!merchantName) return;

        const gen = ++searchGenRef.current;
        setIsLoading(true);
        const merchants = [merchantName];

        try {
          // Step 1: fast keyword search across all categories
          const [contacts, paymentContacts, subscriptionContacts, formsRaw, elementsRaw] =
            await Promise.all([
              searchContacts(merchants, keyword),
              searchTransactionDonors(merchants, keyword),
              searchSubscriptionDonors(merchants, keyword),
              searchForms(merchants, keyword),
              searchElements(merchants, keyword),
            ]);

          if (gen !== searchGenRef.current) return; // discard stale response

          // Deduplicate contacts by contactID (topContacts can return the same person multiple times)
          const dedup = (arr: Contact[]): Contact[] => {
            const seen = new Set<string>();
            return arr.filter((c) => {
              if (seen.has(c.contactID)) return false;
              seen.add(c.contactID);
              return true;
            });
          };

          const slicedPayments = dedup(paymentContacts as Contact[]).slice(0, MAX_PER_CATEGORY);
          const slicedSubscriptions = dedup(subscriptionContacts as Contact[]).slice(
            0,
            MAX_PER_CATEGORY,
          );

          // topForms/topElements return arrays of name strings — we'll enrich to full objects in step 2
          const formNames = (Array.isArray(formsRaw) ? formsRaw : []).slice(
            0,
            MAX_PER_CATEGORY,
          ) as string[];
          const elementNames = (Array.isArray(elementsRaw) ? elementsRaw : []).slice(
            0,
            MAX_PER_CATEGORY,
          ) as string[];

          // Show contact-level results immediately (forms/elements empty until enriched)
          const initialResults: SearchResults = {
            contacts: dedup(contacts as Contact[]).slice(0, MAX_PER_CATEGORY),
            payments: slicedPayments.map((c) => ({ ...c, transactions: [] })),
            subscriptions: slicedSubscriptions.map((c) => ({ ...c, subscriptions: [] })),
            forms: [],
            elements: [],
          };
          setResults(initialResults);
          setIsLoading(false);
          setHasSearched(true);

          // Step 2: enrich with record-level data in parallel
          const paymentContactIDs = slicedPayments.map((c) => c.contactID).filter(Boolean);
          const subscriptionContactIDs = slicedSubscriptions
            .map((c) => c.contactID)
            .filter(Boolean);

          const [transactions, subscriptions, formRecords, elementRecords] = await Promise.all([
            paymentContactIDs.length > 0
              ? fetchRecentTransactions(
                  merchants,
                  paymentContactIDs,
                  MAX_PER_CATEGORY * MAX_TRANSACTIONS_PER_CONTACT,
                )
              : Promise.resolve([]),
            subscriptionContactIDs.length > 0
              ? fetchContactSubscriptions(
                  merchants,
                  subscriptionContactIDs,
                  MAX_PER_CATEGORY * MAX_TRANSACTIONS_PER_CONTACT,
                )
              : Promise.resolve([]),
            formNames.length > 0
              ? fetchFormsByNames(merchants, formNames, MAX_PER_CATEGORY)
              : Promise.resolve([]),
            elementNames.length > 0
              ? fetchElementsByNames(merchants, elementNames, MAX_PER_CATEGORY)
              : Promise.resolve([]),
          ]);

          if (gen !== searchGenRef.current) return; // discard stale response

          // Group transactions by contactID
          const txByContact = new Map<string, Transaction[]>();
          for (const tx of transactions as Transaction[]) {
            const cid = tx.contactID || '';
            if (!txByContact.has(cid)) txByContact.set(cid, []);
            txByContact.get(cid)!.push(tx);
          }

          // Group subscriptions by contactID
          const subByContact = new Map<string, Subscription[]>();
          for (const sub of subscriptions as Subscription[]) {
            const cid = sub.contactID || '';
            if (!subByContact.has(cid)) subByContact.set(cid, []);
            subByContact.get(cid)!.push(sub);
          }

          setResults((prev) => ({
            ...prev,
            payments: slicedPayments.map((c) => ({
              ...c,
              transactions: (txByContact.get(c.contactID) || []).slice(
                0,
                MAX_TRANSACTIONS_PER_CONTACT,
              ),
            })),
            subscriptions: slicedSubscriptions.map((c) => ({
              ...c,
              subscriptions: (subByContact.get(c.contactID) || []).slice(
                0,
                MAX_TRANSACTIONS_PER_CONTACT,
              ),
            })),
            forms: (formRecords as FormResult[]).slice(0, MAX_PER_CATEGORY),
            elements: (elementRecords as ElementResult[]).slice(0, MAX_PER_CATEGORY),
          }));
        } catch (error) {
          console.error('Universal search failed:', error);
          if (gen !== searchGenRef.current) return;
          setIsLoading(false);
          setHasSearched(true);
        }
      },
      [merchantName],
    );

    const handleInputChange = useCallback(
      (value: string) => {
        setQuery(value);
        const trimmed = value.trim();

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (trimmed.length < MIN_QUERY_LENGTH) {
          setResults(EMPTY_RESULTS);
          setIsLoading(false);
          setHasSearched(false);
          return;
        }

        setIsLoading(true);
        debounceRef.current = setTimeout(() => {
          executeSearch(trimmed);
        }, DEBOUNCE_MS);
      },
      [executeSearch],
    );

    const handleSelect = useCallback(
      (section: 'contacts' | 'payments' | 'subscriptions' | 'forms' | 'elements', id: string) => {
        setOpen(false);

        switch (section) {
          case 'contacts':
            history.push(`/contacts?contact=${id}&noSheet=1`);
            break;
          case 'payments':
            history.push(`/payments?contact=${id}`);
            break;
          case 'subscriptions':
            history.push(`/subscriptions?contact=${id}`);
            break;
          case 'forms':
            history.push(`/forms/${id}?noSheet=1`);
            break;
          case 'elements':
            history.push(`/elements/${id}?noSheet=1`);
            break;
        }
      },
      [history],
    );

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setQuery('');
        setResults(EMPTY_RESULTS);
        setIsLoading(false);
        setHasSearched(false);
      }
    };

    const trimmedQuery = query.trim();
    const isBelowMinLength = trimmedQuery.length > 0 && trimmedQuery.length < MIN_QUERY_LENGTH;
    const forms = results.forms ?? [];
    const elements = results.elements ?? [];
    const hasResults =
      results.contacts.length > 0 ||
      results.payments.length > 0 ||
      results.subscriptions.length > 0 ||
      forms.length > 0 ||
      elements.length > 0;
    const showNoResults =
      hasSearched && !hasResults && !isLoading && trimmedQuery.length >= MIN_QUERY_LENGTH;
    // CON-165: with both collapse flags set, the two halves below used to put
    // lg:justify-start AND lg:justify-center (and both lg:px-*) on one button,
    // and Tailwind's stylesheet order picked the winner. v4 orders those pairs
    // differently from v3, so each combination now carries only the classes v3
    // rendered: justify-center with px-2.5 when both flags are set.
    const searchTriggerSizeClass = compact
      ? `${
          collapseToIconBelowLg
            ? `h-8 justify-center px-0 ${collapseToIconAtLg ? '' : 'lg:justify-start '}lg:px-2.5`
            : 'h-8 px-2.5'
        } ${
          collapseToIconAtLg
            ? collapseToIconBelowLg
              ? 'lg:justify-center'
              : 'lg:justify-center lg:px-0'
            : ''
        }`
      : 'h-9 px-3';
    const searchTriggerLabelClass =
      collapseToIconBelowLg && collapseToIconAtLg
        ? 'sr-only'
        : collapseToIconBelowLg
          ? 'sr-only lg:not-sr-only lg:min-w-0 lg:flex-1 lg:truncate lg:text-left'
          : collapseToIconAtLg
            ? 'min-w-0 flex-1 truncate text-left lg:sr-only'
            : 'min-w-0 flex-1 truncate text-left';

    const renderContact = (contact: Contact) => {
      if (contact.name) {
        return (
          <span className="flex min-w-0 items-center">
            <span className="font-medium text-ink truncate">{contact.name}</span>
            <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
            <span className="text-ink-muted truncate">{contact.email}</span>
          </span>
        );
      }
      return <span className="text-ink truncate">{contact.email}</span>;
    };

    const searchTriggerButton = (
      <button
        onClick={() => isEnabled && setOpen(true)}
        disabled={!isEnabled}
        aria-label={placeholder}
        className={`flex w-full min-w-0 items-center gap-2 rounded-md border border-edge bg-white text-sm text-ink-muted shadow-sm shadow-black/[0.02] transition duration-150 hover:border-edge-input hover:text-ink focus:outline-none focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 ${searchTriggerSizeClass}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4 flex-shrink-0"
        >
          <path
            fillRule="evenodd"
            d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
            clipRule="evenodd"
          />
        </svg>
        <span className={searchTriggerLabelClass}>{placeholder}</span>
        {showShortcut && (
          <kbd
            className={`pointer-events-none ml-auto hidden flex-shrink-0 items-center gap-0.5 rounded border border-edge bg-paper-muted px-1.5 py-0.5 font-mono font-medium text-ink-muted sm:inline-flex ${
              collapseToIconAtLg ? 'lg:hidden' : ''
            }`}
          >
            <span className="text-[12px]">⌘</span>
            <span className="text-[10px]">K</span>
          </kbd>
        )}
      </button>
    );

    return (
      <>
        {/* Search trigger button */}
        {showShortcutTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>{searchTriggerButton}</TooltipTrigger>
            <TooltipContent side={tooltipSide}>
              <SearchShortcutTooltip label={placeholder} />
            </TooltipContent>
          </Tooltip>
        ) : (
          searchTriggerButton
        )}

        {/* Command palette dialog */}
        <CommandDialog open={open} onOpenChange={handleOpenChange}>
          <CommandInput
            placeholder="Search..."
            value={query}
            onValueChange={handleInputChange}
            isLoading={isLoading && (hasResults || hasSearched)}
          />
          <CommandList>
            {/* Below minimum length hint */}
            {isBelowMinLength && (
              <div className="py-6 text-center text-sm text-ink-muted">
                Type 2 or more characters to search...
              </div>
            )}

            {/* Initial loading (no prior results) */}
            {isLoading &&
              !hasResults &&
              !isBelowMinLength &&
              trimmedQuery.length >= MIN_QUERY_LENGTH && (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-ink-muted">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Searching...
                </div>
              )}

            {/* No results */}
            {showNoResults && (
              <div className="py-6 text-center text-sm text-ink-muted">
                <p>No results for &ldquo;{trimmedQuery}&rdquo;</p>
                <p className="mt-1 text-xs">Try searching by name or email.</p>
              </div>
            )}

            {/* Contacts */}
            {results.contacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {results.contacts.map((contact) => (
                  <CommandItem
                    key={`contacts-${contact.contactID}`}
                    value={`contacts-${contact.contactID}`}
                    onSelect={() => handleSelect('contacts', contact.contactID)}
                    className="cursor-pointer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="mr-2 h-4 w-4 shrink-0 text-ink-muted"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="truncate">{renderContact(contact)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Payments — contact header with transaction sub-rows */}
            {results.payments.length > 0 && (
              <CommandGroup heading="Payments">
                {results.payments.map((contact) => (
                  <React.Fragment key={`payments-${contact.contactID}`}>
                    {/* Contact header row */}
                    <CommandItem
                      value={`payments-${contact.contactID}`}
                      onSelect={() => handleSelect('payments', contact.contactID)}
                      className="cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="mr-2 h-4 w-4 shrink-0 text-ink-muted"
                      >
                        <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
                        <path
                          fillRule="evenodd"
                          d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="truncate">{renderContact(contact)}</span>
                    </CommandItem>

                    {/* Transaction sub-rows */}
                    {contact.transactions.map((tx) => (
                      <CommandItem
                        key={`tx-${tx.transactionID}`}
                        value={`tx-${tx.transactionID}`}
                        onSelect={() => handleSelect('payments', contact.contactID)}
                        className="cursor-pointer"
                      >
                        <div className="ml-7 flex min-w-0 items-center text-xs">
                          <span className="shrink-0 text-ink-muted">
                            {formatDate(tx.timestamp)}
                          </span>
                          <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                          <span className="shrink-0 font-medium text-ink">
                            {formatCurrency(tx.totalCharged)}
                          </span>
                          <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                          <StatusBadge status={tx.state} />
                        </div>
                      </CommandItem>
                    ))}
                  </React.Fragment>
                ))}
              </CommandGroup>
            )}

            {/* Subscriptions — contact with amount and status */}
            {results.subscriptions.length > 0 && (
              <CommandGroup heading="Subscriptions">
                {results.subscriptions.map((contact) => (
                  <React.Fragment key={`subscriptions-${contact.contactID}`}>
                    {/* Contact header row */}
                    <CommandItem
                      value={`subscriptions-${contact.contactID}`}
                      onSelect={() => handleSelect('subscriptions', contact.contactID)}
                      className="cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="mr-2 h-4 w-4 shrink-0 text-ink-muted"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="truncate">{renderContact(contact)}</span>
                    </CommandItem>

                    {/* Subscription sub-rows */}
                    {contact.subscriptions.map((sub) => (
                      <CommandItem
                        key={`sub-${sub.id}`}
                        value={`sub-${sub.id}`}
                        onSelect={() => handleSelect('subscriptions', contact.contactID)}
                        className="cursor-pointer"
                      >
                        <div className="ml-7 flex min-w-0 items-center text-xs">
                          <span className="shrink-0 font-medium text-ink">
                            {formatCurrency(sub.totalCharged)}
                            {formatFrequency(sub.donationFrequency)}
                          </span>
                          <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                          <StatusBadge status={sub.subscriptionStatus} />
                        </div>
                      </CommandItem>
                    ))}
                  </React.Fragment>
                ))}
              </CommandGroup>
            )}

            {/* Forms */}
            {forms.length > 0 && (
              <CommandGroup heading="Forms">
                {forms.map((form) => (
                  <CommandItem
                    key={`forms-${form.elementID}`}
                    value={`forms-${form.elementID}`}
                    onSelect={() => handleSelect('forms', form.elementID)}
                    className="cursor-pointer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="mr-2 h-4 w-4 shrink-0 text-ink-muted"
                    >
                      <path d="M5.625 3.75a2.625 2.625 0 1 0 0 5.25h12.75a2.625 2.625 0 0 0 0-5.25H5.625ZM3.75 11.25a.75.75 0 0 0 0 1.5h16.5a.75.75 0 0 0 0-1.5H3.75ZM3 15.75a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75ZM3.75 18.75a.75.75 0 0 0 0 1.5h16.5a.75.75 0 0 0 0-1.5H3.75Z" />
                    </svg>
                    <div className="flex min-w-0 items-center">
                      <span className="font-medium text-ink truncate">{form.formName}</span>
                      <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                      <span className="shrink-0 text-ink-muted">
                        {formatFormType(form.formType)}
                      </span>
                      <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                      <StatusBadge status={form.active ? 'ACTIVE' : 'DEACTIVATED'} />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Elements */}
            {elements.length > 0 && (
              <CommandGroup heading="Web Elements">
                {elements.map((element) => (
                  <CommandItem
                    key={`elements-${element.elementID}`}
                    value={`elements-${element.elementID}`}
                    onSelect={() => handleSelect('elements', element.elementID)}
                    className="cursor-pointer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="mr-2 h-4 w-4 shrink-0 text-ink-muted"
                    >
                      <path d="M6 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6ZM15.75 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3H18a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-2.25ZM6 12.75a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3v-2.25a3 3 0 0 0-3-3H6ZM17.625 13.5a.75.75 0 0 0-1.5 0v2.625H13.5a.75.75 0 0 0 0 1.5h2.625v2.625a.75.75 0 0 0 1.5 0v-2.625h2.625a.75.75 0 0 0 0-1.5h-2.625V13.5Z" />
                    </svg>
                    <div className="flex min-w-0 items-center">
                      <span className="font-medium text-ink truncate">{element.elementName}</span>
                      <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                      <span className="shrink-0 text-ink-muted">
                        {formatElementType(element.elementType)}
                      </span>
                      <span className="mx-1.5 shrink-0 text-ink-muted">·</span>
                      <StatusBadge status={element.active ? 'ACTIVE' : 'DEACTIVATED'} />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Empty state when dialog first opens */}
            {!isBelowMinLength && trimmedQuery.length === 0 && (
              <CommandEmpty>
                Start typing to search across contacts, payments, subscriptions, forms, and
                elements.
              </CommandEmpty>
            )}
          </CommandList>
        </CommandDialog>
      </>
    );
  },
);

export default UniversalSearch;
